/**
 * HTML → Markdown.
 *
 * Walks the cleaned DOM and writes GitHub-flavoured Markdown. Anything it has
 * no Markdown equivalent for (a `<sup>`, a nested table) is passed through as
 * the original tag, which is valid Markdown anyway.
 */

import { parseToBody } from './sanitize';

interface Ctx {
  /** Current list nesting, innermost last. */
  lists: Array<{ ordered: boolean; index: number }>;
  /** Inside a blockquote — every emitted line gets a `> ` prefix. */
  quoteDepth: number;
}

export function htmlToMarkdown(html: string): string {
  const body = parseToBody(html);
  const md = renderBlocks(Array.from(body.childNodes), { lists: [], quoteDepth: 0 });
  return md.replace(/\n{3,}/g, '\n\n').trim() + '\n';
}

// ── Block level ──────────────────────────────────────────────────────────────

function renderBlocks(nodes: Node[], ctx: Ctx): string {
  let out = '';
  for (const node of nodes) {
    out += renderBlock(node, ctx);
  }
  return out;
}

function renderBlock(node: Node, ctx: Ctx): string {
  if (node.nodeType === Node.TEXT_NODE) {
    const text = (node as Text).data.replace(/\s+/g, ' ');
    return text.trim() ? escapeMd(text) : '';
  }
  if (node.nodeType !== Node.ELEMENT_NODE) return '';

  const el = node as Element;
  const tag = el.tagName.toLowerCase();
  const kids = Array.from(el.childNodes);

  switch (tag) {
    case 'h1': case 'h2': case 'h3': case 'h4': case 'h5': case 'h6': {
      const level = Number(tag[1]);
      return `\n${'#'.repeat(level)} ${renderInline(kids).trim()}\n\n`;
    }

    case 'p':
      return `\n${renderInline(kids).trim()}\n\n`;

    case 'br':
      return '  \n';

    case 'hr':
      return '\n---\n\n';

    case 'ul': case 'ol':
      return renderList(el, ctx);

    case 'li':
      // A stray <li> outside a list — treat it as a bullet.
      return `- ${renderInline(kids).trim()}\n`;

    case 'blockquote': {
      const inner = renderBlocks(kids, { ...ctx, quoteDepth: ctx.quoteDepth + 1 }).trim();
      const quoted = inner.split('\n').map(l => (l ? `> ${l}` : '>')).join('\n');
      return `\n${quoted}\n\n`;
    }

    case 'pre': {
      const codeEl = el.querySelector('code');
      const lang = codeEl ? languageOf(codeEl) : '';
      const body = (codeEl ?? el).textContent ?? '';
      return `\n\`\`\`${lang}\n${body.replace(/\n+$/, '')}\n\`\`\`\n\n`;
    }

    case 'table':
      return renderTable(el);

    case 'dl': {
      let out = '\n';
      for (const child of Array.from(el.children)) {
        const t = child.tagName.toLowerCase();
        if (t === 'dt') out += `**${renderInline(Array.from(child.childNodes)).trim()}**\n`;
        else if (t === 'dd') out += `: ${renderInline(Array.from(child.childNodes)).trim()}\n`;
      }
      return out + '\n';
    }

    case 'figure': case 'figcaption':
      return `\n${renderBlocks(kids, ctx).trim()}\n\n`;

    case 'div': case 'section': case 'article': case 'main':
    case 'header': case 'footer': case 'aside': case 'nav':
      return renderBlocks(kids, ctx);

    default:
      // Inline element sitting at block level.
      return renderInline([el]);
  }
}

function renderList(list: Element, ctx: Ctx): string {
  const ordered = list.tagName.toLowerCase() === 'ol';
  const start = Number(list.getAttribute('start') ?? '1') || 1;
  const depth = ctx.lists.length;
  const indent = '  '.repeat(depth);

  let index = start;
  let out = depth === 0 ? '\n' : '\n';

  for (const li of Array.from(list.children)) {
    if (li.tagName.toLowerCase() !== 'li') continue;

    const marker = ordered ? `${index}.` : '-';
    index++;

    // Split the <li> into its inline lead-in and any nested blocks.
    const inlineParts: Node[] = [];
    const blockParts: Node[] = [];
    for (const child of Array.from(li.childNodes)) {
      const t = child.nodeType === Node.ELEMENT_NODE ? (child as Element).tagName.toLowerCase() : '';
      if (t === 'ul' || t === 'ol' || t === 'pre' || t === 'blockquote' || t === 'table') blockParts.push(child);
      else inlineParts.push(child);
    }

    const lead = renderInline(inlineParts).trim();
    out += `${indent}${marker} ${lead}\n`;

    for (const block of blockParts) {
      const nested = { ...ctx, lists: [...ctx.lists, { ordered, index }] };
      const rendered = renderBlock(block, nested);
      if (!rendered.trim()) continue;
      const t = (block as Element).tagName?.toLowerCase();
      if (t === 'ul' || t === 'ol') {
        // A nested list already carries its own indent — only trim blank lines
        // off the ends, never the leading spaces of the first item.
        out += rendered.replace(/^\n+/, '').replace(/\n+$/, '') + '\n';
      } else {
        // Other blocks need a 4-space continuation indent to stay in the item.
        out += rendered.trim().split('\n').map(l => `${indent}    ${l}`).join('\n') + '\n';
      }
    }
  }

  return out + (depth === 0 ? '\n' : '');
}

function renderTable(table: Element): string {
  const rows: string[][] = [];
  let headerCount = 0;

  for (const tr of Array.from(table.querySelectorAll('tr'))) {
    const cells = Array.from(tr.children)
      .filter(c => /^(td|th)$/i.test(c.tagName))
      .map(c => renderInline(Array.from(c.childNodes)).replace(/\|/g, '\\|').replace(/\n/g, ' ').trim());
    if (!cells.length) continue;
    rows.push(cells);
    if (rows.length === 1 && Array.from(tr.children).every(c => c.tagName.toLowerCase() === 'th')) headerCount = 1;
  }

  if (!rows.length) return '';

  const width = Math.max(...rows.map(r => r.length));
  const pad = (r: string[]): string[] => [...r, ...Array(width - r.length).fill('')];

  // Markdown pipe tables always need a header row — synthesise a blank one if
  // the source table had no <th>.
  const head = headerCount ? pad(rows[0]) : Array(width).fill('');
  const bodyRows = headerCount ? rows.slice(1) : rows;

  let out = `\n| ${head.join(' | ')} |\n| ${Array(width).fill('---').join(' | ')} |\n`;
  for (const r of bodyRows) out += `| ${pad(r).join(' | ')} |\n`;
  return out + '\n';
}

// ── Inline level ─────────────────────────────────────────────────────────────

function renderInline(nodes: Node[]): string {
  let out = '';
  for (const node of nodes) {
    if (node.nodeType === Node.TEXT_NODE) {
      out += escapeMd((node as Text).data.replace(/\s+/g, ' '));
      continue;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) continue;

    const el = node as Element;
    const tag = el.tagName.toLowerCase();
    const kids = Array.from(el.childNodes);

    switch (tag) {
      case 'strong': case 'b': out += wrap(renderInline(kids), '**'); break;
      case 'em': case 'i': out += wrap(renderInline(kids), '*'); break;
      case 'del': case 's': case 'strike': out += wrap(renderInline(kids), '~~'); break;
      case 'code': out += renderInlineCode(el); break;
      case 'br': out += '  \n'; break;
      case 'a': {
        const href = el.getAttribute('href');
        const text = renderInline(kids).trim() || href || '';
        out += href ? `[${text}](${href})` : text;
        break;
      }
      case 'img': {
        const alt = el.getAttribute('alt') ?? '';
        const src = el.getAttribute('src') ?? '';
        out += src ? `![${alt}](${src})` : '';
        break;
      }
      case 'span': case 'font': case 'u': case 'small': case 'mark':
      case 'abbr': case 'cite': case 'time': case 'q': case 'bdi': case 'bdo':
        out += renderInline(kids);
        break;
      // Blocks can appear inside a paragraph in messy markup — recurse rather
      // than losing them.
      case 'p': case 'div': out += renderInline(kids); break;
      case 'sup': out += `<sup>${renderInline(kids)}</sup>`; break;
      case 'sub': out += `<sub>${renderInline(kids)}</sub>`; break;
      default: out += renderInline(kids); break;
    }
  }
  return out;
}

/** Bold/italic markers only work when they hug non-space text. */
function wrap(text: string, marker: string): string {
  const match = /^(\s*)([\s\S]*?)(\s*)$/.exec(text);
  if (!match || !match[2]) return text;
  return `${match[1]}${marker}${match[2]}${marker}${match[3]}`;
}

function renderInlineCode(el: Element): string {
  const text = el.textContent ?? '';
  if (!text) return '';
  // Use enough backticks to out-fence any run inside the code itself.
  const longest = (text.match(/`+/g) ?? []).reduce((m, r) => Math.max(m, r.length), 0);
  const fence = '`'.repeat(longest + 1);
  const padding = text.startsWith('`') || text.endsWith('`') ? ' ' : '';
  return `${fence}${padding}${text}${padding}${fence}`;
}

function languageOf(codeEl: Element): string {
  const cls = codeEl.getAttribute('class') ?? '';
  const match = /(?:language|lang)-([\w+#-]+)/i.exec(cls);
  return match ? match[1] : '';
}

/** Escape the characters that would otherwise turn plain text into markup. */
function escapeMd(text: string): string {
  return text
    .replace(/([\\`*_[\]])/g, '\\$1')
    .replace(/^(\s*)(#{1,6}\s)/gm, '$1\\$2')
    .replace(/^(\s*)([-+]\s)/gm, '$1\\$2')
    .replace(/^(\s*)(\d+)\.\s/gm, '$1$2\\. ');
}
